/**
 * Registers /sw.js in production builds only (import.meta.env.PROD guard).
 *
 * Update flow:
 *   1. A new service worker reaches `installed` while a controller exists →
 *      an `updateAvailable` event is emitted with the waiting worker.
 *   2. PwaUpdatePrompt shows the Dutch update banner; on 'Vernieuwen' it posts
 *      SKIP_WAITING to the waiting worker and reloads on `controllerchange`.
 *   The worker itself never calls skipWaiting() unprompted (see public/sw.js).
 */
import { emitPwaEvent } from './pwa-events'

let registered = false

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return
  if (registered) return
  registered = true

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // A worker is already waiting (e.g. page open across an update).
        if (registration.waiting && navigator.serviceWorker.controller) {
          emitPwaEvent('updateAvailable', {
            worker: registration.waiting,
            registration,
          })
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing
          if (!installing) return
          installing.addEventListener('statechange', () => {
            // Only treat it as an app update when there is a live controller;
            // otherwise it is the very first install.
            if (
              installing.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              emitPwaEvent('updateAvailable', {
                worker: installing,
                registration,
              })
            }
          })
        })
      })
      .catch(() => {
        // Registration failure must never break the app shell.
      })
  })
}
